import axios from "axios";
const BASE_URL = "https://front.microcentrum.cl/v1/surveys";
const SURVEY_SESSION_TOKEN_KEY = "surveyResponseAccessToken";
/** Token Cognito o accessToken opaco `ss_…` de sesión web (Android). */
export const getSurveyAuthToken = () => {
    return (sessionStorage.getItem(SURVEY_SESSION_TOKEN_KEY) ||
        localStorage.getItem("authToken") ||
        "");
};
export const setSurveyResponseAccessToken = (token) => {
    if (!token) {
        sessionStorage.removeItem(SURVEY_SESSION_TOKEN_KEY);
        return;
    }
    sessionStorage.setItem(SURVEY_SESSION_TOKEN_KEY, token);
};
const authHeaders = () => {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: getSurveyAuthToken(),
    };
};
/** Normaliza encuesta API (target singular) → modelo admin (audienceTargets). */
export const normalizeSurveyAdmin = (raw) => {
    const target = raw.target;
    const audienceTargets = raw.audienceTargets ||
        (target ? [target] : []);
    const anonymous = Boolean(raw.anonymous);
    const responseVisibility = raw.responseVisibility ||
        (anonymous ? "ANONYMOUS_TO_MANAGERS" : "IDENTIFIED");
    const responseChannel = raw.responseChannel ||
        (audienceTargets.some((target) => target.audienceType === "FAIR_STANDS")
            ? "CMS"
            : "APP");
    return {
        surveyId: String(raw.surveyId ?? ""),
        schoolId: String(raw.schoolId ?? ""),
        title: String(raw.title ?? ""),
        description: raw.description,
        introductionMessage: raw.introductionMessage,
        confirmationMessage: raw.confirmationMessage,
        inviteEmailSubject: raw.inviteEmailSubject,
        inviteEmailBody: raw.inviteEmailBody,
        allowDecline: Boolean(raw.allowDecline),
        audienceTargets,
        responseRequirement: raw.responseRequirement ||
            "OPTIONAL",
        responseVisibility,
        responseChannel,
        responseUnit: raw.responseUnit === "COURSE" ? "COURSE" : "RECIPIENT",
        responseUnitCount: raw.responseUnitCount === undefined || raw.responseUnitCount === null
            ? undefined
            : Number(raw.responseUnitCount),
        questions: raw.questions || [],
        startDate: raw.startDate ?? null,
        endDate: raw.endDate ?? null,
        editResponsesAfterSubmit: Boolean(raw.editResponsesAfterSubmit),
        showResultsToRespondent: Boolean(raw.showResultsToRespondent),
        notificationChannels: raw.notificationChannels || [],
        version: Number(raw.version ?? 1),
        recipientCount: Number(raw.recipientCount ?? 0),
        responseCount: Number(raw.responseCount ?? 0),
        anonymous,
        allowMultipleResponses: Boolean(raw.allowMultipleResponses),
        status: raw.status || "DRAFT",
        publicationStatus: raw.publicationStatus,
        publicationError: raw.publicationError,
        courseId: raw.courseId,
        courseCode: raw.courseCode,
        year: raw.year,
    };
};
/** Payload create/update: agrega compatibilidad legacy solo si el campo fue enviado. */
const toApiPayload = (payload) => {
    const firstTarget = payload.audienceTargets?.[0];
    const apiPayload = { ...payload };
    if (payload.anonymous !== undefined ||
        payload.responseVisibility !== undefined) {
        apiPayload.anonymous =
            payload.anonymous ??
                payload.responseVisibility === "ANONYMOUS_TO_MANAGERS";
    }
    if (payload.audienceTargets !== undefined) {
        apiPayload.target = firstTarget;
    }
    return apiPayload;
};
export const fetchMySurveysForCourse = async (params) => {
    const qs = new URLSearchParams({
        by: "me",
        school_id: params.school_id,
        year: String(params.year),
        course_code: params.course_code,
    });
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    return (response.data.surveys ?? []);
};
/** Bandeja por SurveyRecipient (publicaciones Fase 2+). */
export const fetchSurveyInbox = async (params) => {
    const qs = new URLSearchParams({
        by: "inbox",
        response_channel: "APP",
    });
    if (params?.schoolId)
        qs.set("school_id", params.schoolId);
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    return {
        surveys: response.data.surveys ?? [],
        pendingCount: Number(response.data.pendingCount ?? 0),
        requiredPendingCount: Number(response.data.requiredPendingCount ?? 0),
    };
};
export const createWebResponseSession = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=createWebResponseSession`, { surveyId }, { headers: authHeaders() });
    return response.data;
};
/** Sin Cognito: intercambia token de traspaso por accessToken temporal. */
export const exchangeWebResponseSession = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=exchangeWebResponseSession`, {
        surveyId: params.surveyId,
        sessionToken: params.sessionToken,
    }, {
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    return response.data;
};
export const submitSurveyResponse = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=respond`, payload, {
        headers: authHeaders(),
    });
    return response.data;
};
export const submitSurveyTestResponse = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=respondTest`, payload, { headers: authHeaders() });
    return response.data;
};
export const fetchMySurveyResponse = async (surveyId) => {
    const qs = new URLSearchParams({
        by: "myResponse",
        survey_id: surveyId,
    });
    try {
        const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
        const r = response.data?.response;
        if (!r) {
            return null;
        }
        return r;
    }
    catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
            return null;
        }
        throw e;
    }
};
export const updateMySurveyResponse = async (payload) => {
    await axios.post(`${BASE_URL}?op=updateMyResponse`, payload, {
        headers: authHeaders(),
    });
};
/** Agregados únicamente (by=resultsSummary; by=results es alias). */
export const fetchSurveyResultsSummary = async (surveyId) => {
    const qs = new URLSearchParams({
        by: "resultsSummary",
        survey_id: surveyId,
    });
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    return response.data;
};
/** Descarga CSV de respuestas (solo gestores). */
export const downloadSurveyResponsesCsv = async (surveyId) => {
    const qs = new URLSearchParams({
        by: "exportCsv",
        survey_id: surveyId,
    });
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, {
        headers: {
            ...authHeaders(),
            Accept: "text/csv",
        },
        responseType: "blob",
    });
    const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `encuesta_${surveyId}_respuestas.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
export const computeRankingAssignment = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=computeRankingAssignment`, payload, { headers: authHeaders() });
    return response.data;
};
export const removeSurveyRankingOptions = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=removeRankingOptions`, payload, { headers: authHeaders() });
    return response.data;
};
export const getRankingAssignmentConfig = async (params) => {
    const qs = new URLSearchParams({
        op: "rankingAssignmentConfig",
        survey_id: params.surveyId,
        question_id: params.questionId,
    });
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    return response.data.config;
};
export const saveRankingAssignmentConfig = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=saveRankingAssignmentConfig`, payload, { headers: authHeaders() });
    return response.data.config;
};
export const sendRankingAssignmentEmails = async (payload) => {
    const response = await axios.post(`${BASE_URL}?op=sendRankingAssignmentEmails`, payload, { headers: authHeaders() });
    return response.data;
};
/** Respuestas individuales — solo gestores (by=responses). */
export const fetchSurveyResponses = async (surveyId) => {
    const allResponses = [];
    let lastEvaluatedKey;
    do {
        const qs = new URLSearchParams({
            by: "responses",
            survey_id: surveyId,
            limit: "100",
        });
        if (lastEvaluatedKey) {
            qs.set("last_evaluated_key", lastEvaluatedKey);
        }
        const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
        allResponses.push(...(response.data.responses ?? []));
        lastEvaluatedKey = response.data.last_evaluated_key;
    } while (lastEvaluatedKey);
    return allResponses;
};
/**
 * Resumen + respuestas si el usuario es gestor.
 * Los respondentes solo reciben agregados (sin lista individual).
 */
export const fetchSurveyResults = async (surveyId) => {
    const summary = await fetchSurveyResultsSummary(surveyId);
    try {
        const responses = await fetchSurveyResponses(surveyId);
        return { ...summary, responses };
    }
    catch (e) {
        if (axios.isAxiosError(e) &&
            (e.response?.status === 403 || e.response?.status === 401)) {
            return summary;
        }
        throw e;
    }
};
export const listManageableSurveys = async (params) => {
    const qs = new URLSearchParams({
        by: "manageable",
        school_id: params.schoolId,
    });
    if (params.status) {
        qs.set("status", params.status);
    }
    if (params.courseCode) {
        qs.set("course_code", params.courseCode);
    }
    if (params.year) {
        qs.set("year", params.year);
    }
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    const surveys = response.data.surveys ?? [];
    return surveys.map((s) => normalizeSurveyAdmin(s));
};
export const getSurvey = async (surveyId) => {
    const qs = new URLSearchParams({
        by: "id",
        survey_id: surveyId,
    });
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    const survey = response.data.survey ?? response.data;
    return normalizeSurveyAdmin(survey);
};
export const createSurvey = async (payload) => {
    const response = await axios.post(BASE_URL, toApiPayload(payload), { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
const cloneEntityId = (prefix) => `${prefix}_${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 12)}`;
/** Crea un borrador independiente a partir de una encuesta existente. */
export const cloneSurvey = async (surveyId) => {
    const source = await getSurvey(surveyId);
    return createSurvey({
        schoolId: source.schoolId,
        courseId: source.courseId,
        courseCode: source.courseCode,
        year: source.year,
        title: `${source.title || "Encuesta"} (copia)`,
        description: source.description,
        introductionMessage: source.introductionMessage,
        confirmationMessage: source.confirmationMessage,
        inviteEmailSubject: source.inviteEmailSubject,
        inviteEmailBody: source.inviteEmailBody,
        audienceTargets: (source.audienceTargets || []).map((target) => ({
            ...target,
            targetId: cloneEntityId("tgt"),
            excludedEmails: target.excludedEmails
                ? [...target.excludedEmails]
                : undefined,
        })),
        responseRequirement: source.responseRequirement,
        responseVisibility: source.responseVisibility,
        responseChannel: source.responseChannel,
        responseUnit: source.responseUnit === "COURSE" ? "COURSE" : "RECIPIENT",
        questions: (source.questions || []).map((question) => ({
            ...question,
            questionId: cloneEntityId("q"),
            options: question.options?.map((option, index) => ({
                ...option,
                optionId: cloneEntityId("op"),
                value: undefined,
                order: index + 1,
            })),
        })),
        startDate: source.startDate,
        endDate: source.endDate,
        editResponsesAfterSubmit: source.editResponsesAfterSubmit,
        showResultsToRespondent: source.showResultsToRespondent,
        notificationChannels: source.notificationChannels
            ? [...source.notificationChannels]
            : [],
        anonymous: source.responseVisibility === "ANONYMOUS_TO_MANAGERS",
        allowMultipleResponses: false,
    });
};
export const updateSurvey = async (payload) => {
    const response = await axios.put(BASE_URL, toApiPayload(payload), { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
/** Actualiza solo la plantilla del email de invitación (sin tocar preguntas). */
export const updateSurveyInviteEmail = async (params) => {
    const response = await axios.put(BASE_URL, {
        surveyId: params.surveyId,
        version: params.version,
        inviteEmailSubject: params.inviteEmailSubject,
        inviteEmailBody: params.inviteEmailBody,
        allowDecline: params.allowDecline,
    }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
export const previewSurveyInviteEmail = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=previewSurveyInviteEmail`, params, { headers: authHeaders() });
    return response.data;
};
export const sendSurveyInviteEmailTest = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=sendSurveyInviteEmailTest`, params, { headers: authHeaders() });
    return response.data;
};
export const previewAudience = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=previewAudience`, {
        schoolId: params.schoolId,
        year: params.year,
        audienceTargets: params.audienceTargets,
        ...(params.responseUnit ? { responseUnit: params.responseUnit } : {}),
    }, { headers: authHeaders() });
    const data = response.data;
    const estimated = Number(data.estimatedRecipients ?? data.recipientCount ?? 0);
    const estimatedResponseUnits = Number(data.estimatedResponseUnits ?? 0);
    const estimatedMaxResponses = Number(data.estimatedMaxResponses ?? estimated);
    return {
        estimatedRecipients: estimated,
        recipientCount: estimated,
        estimatedResponseUnits,
        estimatedMaxResponses,
        byTarget: data.byTarget,
        duplicatesRemoved: data.duplicatesRemoved,
        warnings: data.warnings,
        breakdown: data.breakdown,
        estimated: true,
    };
};
export const publishSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=publish`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
export const retryPublishSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=retryPublish`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
export const listSurveyRecipients = async (params) => {
    const qs = new URLSearchParams({
        by: "recipients",
        survey_id: params.surveyId,
    });
    if (params.status)
        qs.set("status", params.status);
    const response = await axios.get(`${BASE_URL}?${qs.toString()}`, { headers: authHeaders() });
    return (response.data.recipients ?? []).map((r) => ({
        ...r,
        userEmail: String(r.userEmail || "")
            .trim()
            .toLowerCase(),
        courseCodes: Array.isArray(r.courseCodes) ? r.courseCodes : undefined,
        directiveRoles: Array.isArray(r.directiveRoles)
            ? r.directiveRoles
            : undefined,
    }));
};
export const listPendingSurveyRecipients = async (surveyId) => listSurveyRecipients({ surveyId, status: "PENDING" });
export const closeSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=close`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
export const reopenSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=reopen`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
export const deleteSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=delete`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
/** Recordatorio EMAIL/PUSH a destinatarios PENDING. */
export const remindSurveyRecipients = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=remind`, params, { headers: authHeaders() });
    const data = response.data;
    return {
        pendingCount: Number(data.pendingCount ?? 0),
        emailed: Number(data.emailed ?? 0),
        pushed: Number(data.pushed ?? 0),
    };
};
/** Borra la respuesta de un destinatario, lo deja PENDING y reenvía el email. */
export const resetSurveyRecipientResponse = async (params) => {
    const response = await axios.post(`${BASE_URL}?op=resetResponse`, params, { headers: authHeaders() });
    const data = response.data;
    return {
        userEmail: String(data.userEmail ?? params.userEmail),
        reset: Boolean(data.reset),
        emailed: Number(data.emailed ?? 0),
        pushed: Number(data.pushed ?? 0),
    };
};
export const archiveSurvey = async (surveyId) => {
    await axios.post(`${BASE_URL}?op=archive`, { surveyId }, { headers: authHeaders() });
};
export const duplicateSurvey = async (surveyId) => {
    const response = await axios.post(`${BASE_URL}?op=duplicate`, { surveyId }, { headers: authHeaders() });
    const data = response.data;
    const survey = data.survey ?? data;
    return normalizeSurveyAdmin(survey);
};
